import { useState, useEffect } from 'react'
import { DEFAULT_FILTER_STATE, type FilterState } from '../types/filters.types'

const FILTER_STORAGE_KEY = 'orders-filters'

export const useFilterPersistence = () => {
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTER_STATE)

  // Load filters from localStorage on mount
  useEffect(() => {
    try {
      const savedFilters = localStorage.getItem(FILTER_STORAGE_KEY)
      if (savedFilters && savedFilters.trim()) {
        // Validate JSON structure before parsing
        if (!savedFilters.startsWith('{') || !savedFilters.endsWith('}')) {
          throw new Error('Invalid filter data format')
        }
        const parsed = JSON.parse(savedFilters)
        
        // Validate parsed data structure and sanitize
        const sanitizedFilters: Partial<FilterState> = {}
        const allowedKeys = ['status', 'payment', 'category', 'dateMode', 'dateRange', 'selectedDates']
        
        for (const [key, value] of Object.entries(parsed)) {
          if (allowedKeys.includes(key)) {
            ;(sanitizedFilters as Record<string, unknown>)[key] = value
          }
        }
        
        // Convert date strings back to Date objects with validation
        if (sanitizedFilters.dateRange) {
          try {
            sanitizedFilters.dateRange = {
              start: new Date(sanitizedFilters.dateRange.start),
              end: new Date(sanitizedFilters.dateRange.end)
            }
          } catch {
            delete sanitizedFilters.dateRange
          }
        }
        if (Array.isArray(sanitizedFilters.selectedDates)) {
          try {
            sanitizedFilters.selectedDates = (sanitizedFilters.selectedDates as unknown as string[])
              .map((dateStr: string) => new Date(dateStr))
              .filter(date => !isNaN(date.getTime()))
          } catch {
            delete sanitizedFilters.selectedDates
          }
        }
        
        setFilters({ ...DEFAULT_FILTER_STATE, ...sanitizedFilters })
      }
    } catch {
      setFilters(DEFAULT_FILTER_STATE)
    }
  }, [])

  // Save filters to localStorage whenever they change
  useEffect(() => {
    try {
      // Don't save default state
      const isDefaultState = 
        filters.status === DEFAULT_FILTER_STATE.status &&
        filters.payment === DEFAULT_FILTER_STATE.payment &&
        filters.category === DEFAULT_FILTER_STATE.category &&
        filters.dateMode === DEFAULT_FILTER_STATE.dateMode &&
        filters.selectedDates.length === 0

      if (isDefaultState) {
        localStorage.removeItem(FILTER_STORAGE_KEY)
      } else {
        localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters))
      }
    } catch {
      // Silently fail if localStorage is not available
    }
  }, [filters])

  const updateFilters = (newFilters: Partial<FilterState>) => {
    setFilters(prev => ({ ...prev, ...newFilters }))
  }

  const clearFilters = () => {
    setFilters(DEFAULT_FILTER_STATE)
    localStorage.removeItem(FILTER_STORAGE_KEY)
  }

  return {
    filters,
    updateFilters,
    clearFilters
  }
}